/**
 * Подраздел «Инфраструктурные проекты» раздела «Проекты» (US-05.1):
 * верхняя плашка из 5 показателей (FR-2) + виджеты в порядке постановки —
 * реестр (FR-3) → структура портфеля (FR-4) → отрасли (FR-5) → способы
 * закупки (FR-6) → освоение по АБП (FR-7) → крупнейшие / риск (FR-8);
 * карточка проекта (FR-9) открывается из реестра и риск-виджета.
 *
 * Источник данных — гибрид (как секции «Аналитики»): любой областной
 * КАТО (isProjectsDemoKato, включая пилотный ЗКО) → мок
 * hardData/projectsInfrastructure.json; остальные КАТО (районы и ниже) —
 * запрос /api/public/projects/infrastructure. Эндпоинт на бэке ещё не
 * реализован: до его появления запрос падает, данные считаются
 * отсутствующими — виджеты скрываются (FR-4.1), реестр показывается
 * пустым с подписью (FR-3.5), без ошибки интерфейса.
 *
 * Показатель плашки без данных не отображается, остальные растягиваются
 * (FR-2.6); видимость виджетов — через модули супер-админа (FR-10,
 * useModulePresent, default-visible).
 */
import { Construction } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AbpWidget } from './AbpWidget';
import { fmt, fmt1, moneyMln } from './format';
import { IndustriesWidget } from './IndustriesWidget';
import { PortfolioWidget } from './PortfolioWidget';
import { ProcurementWidget } from './ProcurementWidget';
import { ProjectCardDialog } from './ProjectCardDialog';
import { ProjectsRegistry } from './ProjectsRegistry';
import { TopRiskWidget } from './TopRiskWidget';
import { BlockLoader } from '../../../components/common/BlockLoader';
import { StatCard } from '../../../components/widgets/StatCard';
import { useKato } from '../../../contexts/KatoContext';
import { useYear } from '../../../contexts/YearContext';
import { isProjectsDemoKato } from '../../../hardData/constants';
import projectsInfrastructure from '../../../hardData/projectsInfrastructure.json';
import { useModulePresent } from '../../../hooks/useModulePresent';
import { getProjectsInfrastructure } from '../../../requests/projects.request';
import type {
  InfrastructureProject,
  InfrastructureProjectsData,
} from '../../../types/projects';

const EMPTY: InfrastructureProjectsData = {
  year: null,
  plashka: {
    projectsCount: null,
    contractsCount: null,
    completedCount: null,
    planTotalMln: null,
    yearDevelopmentPct: null,
    remainMln: null,
    tenderSharePct: null,
    tenderContractsCount: null,
  },
  works: [],
  industries: [],
  procurement: [],
  abp: [],
  projects: [],
};

export const InfrastructureProjectsSection = () => {
  const { t } = useTranslation();
  const { selectedKato } = useKato();
  const { selectedYear } = useYear();

  const isHardKato = isProjectsDemoKato(selectedKato);

  const [data, setData] = useState<InfrastructureProjectsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [openedProject, setOpenedProject] =
    useState<InfrastructureProject | null>(null);

  // FR-10: видимость виджетов задаёт супер-админ; без записи в БД —
  // default-visible (useModulePresent).
  const showRegistry = useModulePresent('projects.infrastructure-registry');
  const showPortfolio = useModulePresent('projects.infrastructure-portfolio');
  const showIndustries = useModulePresent('projects.infrastructure-industries');
  const showProcurement = useModulePresent(
    'projects.infrastructure-procurement',
  );
  const showAbp = useModulePresent('projects.infrastructure-abp');
  const showTopRisk = useModulePresent('projects.infrastructure-top-risk');

  // FR-11.1: смена территории (и года в шапке) перезапрашивает данные.
  useEffect(() => {
    if (isHardKato || selectedKato === null) return;
    let cancelled = false;
    setLoading(true);
    getProjectsInfrastructure(selectedKato, selectedYear)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err) => {
        // Реста ещё нет / Power BI недоступен → деградация без ошибки UI.
        if (!cancelled) {
          console.error(
            'InfrastructureProjectsSection getProjectsInfrastructure failed',
            err,
          );
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedKato, isHardKato, selectedYear]);

  const d: InfrastructureProjectsData = isHardKato
    ? (projectsInfrastructure as InfrastructureProjectsData)
    : (data ?? EMPTY);
  const { plashka } = d;

  // --- Плашка (FR-2): показатель без данных не выводится (FR-2.6) ---
  const money = (vMln: number): { value: string; unit: string } => {
    const parts = moneyMln(vMln);
    return {
      value: parts.value,
      unit: t(parts.mlrd ? 'projects.units.mlrdTg' : 'projects.units.mlnTg'),
    };
  };

  // Карточки видны всегда: показатель без данных — название + «нет данных»
  // (замечание ревью от 14.08.2026; StatCard прячет единицу и футеры сам).
  const planTotal =
    plashka.planTotalMln !== null ? money(plashka.planTotalMln) : null;
  const remain = plashka.remainMln !== null ? money(plashka.remainMln) : null;
  const cards: Array<{
    key: string;
    value: string | null;
    unit: string | null;
    footnote: string | null;
  }> = [
    {
      key: 'projects',
      value:
        plashka.projectsCount === null
          ? null
          : plashka.contractsCount !== null
            ? `${fmt(plashka.projectsCount)} / ${fmt(plashka.contractsCount)}`
            : fmt(plashka.projectsCount),
      unit: t('projects.units.pcs'),
      footnote:
        plashka.completedCount !== null
          ? t('projects.infrastructure.cards.completed', {
              value: plashka.completedCount,
            })
          : null,
    },
    {
      key: 'planTotal',
      value: planTotal?.value ?? null,
      unit: planTotal?.unit ?? null,
      footnote: t('projects.infrastructure.cards.planTotalSub'),
    },
    {
      key: 'yearDevelopment',
      value:
        plashka.yearDevelopmentPct !== null
          ? `${fmt1(plashka.yearDevelopmentPct)}%`
          : null,
      unit: null,
      footnote: t('projects.infrastructure.cards.yearDevelopmentSub'),
    },
    {
      key: 'remain',
      value: remain?.value ?? null,
      unit: remain?.unit ?? null,
      footnote: t('projects.infrastructure.cards.remainSub'),
    },
    {
      key: 'tenderShare',
      value:
        plashka.tenderSharePct !== null
          ? `${fmt1(plashka.tenderSharePct)}%`
          : null,
      unit: null,
      footnote:
        plashka.tenderContractsCount !== null &&
        plashka.contractsCount !== null
          ? t('projects.infrastructure.cards.tenderShareSub', {
              tender: plashka.tenderContractsCount,
              total: plashka.contractsCount,
            })
          : null,
    },
  ];

  // Виджет без данных не отображается (FR-4.1, кроме реестра — FR-3.5).
  const hasWorks = d.works.length > 0;
  const hasIndustries = d.industries.length > 0;
  const hasProcurement = d.procurement.length > 0;
  const hasAbp = d.abp.length > 0;
  const hasProjects = d.projects.length > 0;

  return (
    <section className="space-y-4" data-testid="projects-infrastructure">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('projects.infrastructure.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <Construction
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {loading ? (
        <BlockLoader />
      ) : (
        <>
          {cards.length > 0 && (
            <div className="flex flex-wrap gap-3.5">
              {cards.map((card) => (
                <div key={card.key} className="min-w-52 flex-1">
                  <StatCard
                    testId={`projects-card-${card.key}`}
                    title={t(`projects.infrastructure.cards.${card.key}`)}
                    value={card.value}
                    unit={card.unit}
                    footnotes={
                      card.footnote !== null ? [{ text: card.footnote }] : []
                    }
                  />
                </div>
              ))}
            </div>
          )}

          {showRegistry && (
            <ProjectsRegistry projects={d.projects} onOpen={setOpenedProject} />
          )}

          <div className="grid gap-3.5 xl:grid-cols-2">
            {showPortfolio && hasWorks && <PortfolioWidget works={d.works} />}
            {showIndustries && hasIndustries && (
              <IndustriesWidget industries={d.industries} />
            )}
            {showProcurement && hasProcurement && (
              <ProcurementWidget procurement={d.procurement} />
            )}
            {showAbp && hasAbp && <AbpWidget abp={d.abp} />}
          </div>

          {showTopRisk && hasProjects && (
            <TopRiskWidget projects={d.projects} onOpen={setOpenedProject} />
          )}
        </>
      )}

      <ProjectCardDialog
        project={openedProject}
        year={d.year}
        onClose={() => setOpenedProject(null)}
      />
    </section>
  );
};
